import type { JobType } from '@xdocs/schemas';

/**
 * The fixed bookforge checkpoint pipeline per `JobType` (CODE_GUIDELINES
 * §0.2/E11, FEATURES.md §0.2, CLAUDE_DESIGN.md §6 "Job Progress surface" —
 * all three specs pin the SAME stage lists). `jobs.job_checkpoints` writes
 * one row per completed stage named from this exact vocabulary; `currentCheckpoint`
 * on the `jobSnapshot.job` is the currently-running stage's name (or the last
 * completed one once terminal). Used purely for the client-side stepper —
 * never sent to the server.
 */
export const PIPELINE_STAGES: Partial<Record<JobType, readonly string[]>> = {
  outline: ['research_sources', 'download', 'extract_text', 'derive_outline'],
  draft: ['plan_chapters', 'batch_generate_chapters', 'assemble_manuscript', 'write_version'],
  review: ['critique', 'emit_patches', 'apply_patches_deterministic', 'finalize_version'],
  illustrate: ['illustrate'],
  render: ['render'],
  // F7/F6/F18 ingest pipeline (explorer uploads + Drive imports) —
  // FEATURES.md F7: "Worker: `ingest-worker` (`fetch_or_receive →
  // extract_text → thumbnail_sharp → register_file`)".
  ingest: ['fetch_or_receive', 'extract_text', 'thumbnail_sharp', 'register_file'],
};

/** Best-effort stage list for a job type; falls back to just the job's own
 * `currentCheckpoint` (a single-stage stepper) for types without a pinned
 * pipeline in this table (e.g. `expand`/`translate` — not yet documented
 * stage-by-stage in the specs at M1). */
export function stagesForJobType(type: JobType, currentCheckpoint: string | null): readonly string[] {
  const pinned = PIPELINE_STAGES[type];
  if (pinned) return pinned;
  return currentCheckpoint ? [currentCheckpoint] : [];
}
